import React, { useEffect, useMemo, useState } from 'react';
import './App.css';
import {
  activeComps,
  buildingFacts,
  buildingTimeline,
  comparables,
  constraints,
  contradictions,
  evidenceRecords,
  historicalComps,
  lastRefresh,
  leasedOffMarketComps,
  recommendationScenarios,
  sources,
  type CitationRef,
  type ComparableRecord,
  type CompOutcomeStatus,
  type SourceRecord,
  units,
} from './data/propertyData';

type RouteKey =
  | 'overview'
  | 'recommendation'
  | 'units'
  | 'building-history'
  | 'market'
  | 'constraints'
  | 'evidence'
  | 'memo';

type MarketFilter =
  | 'all'
  | 'active'
  | 'leased'
  | 'historical'
  | 'calibration';

const routeOrder: { key: RouteKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'recommendation', label: 'Recommendation' },
  { key: 'units', label: 'Units' },
  { key: 'building-history', label: 'Building History' },
  { key: 'market', label: 'Market' },
  { key: 'constraints', label: 'Constraints' },
  { key: 'evidence', label: 'Evidence' },
  { key: 'memo', label: 'Memo' },
];

const routeTitles: Record<RouteKey, string> = {
  overview: 'Owner Dashboard',
  recommendation: 'Pricing Recommendation',
  units: 'Unit Intelligence',
  'building-history': 'Public Trace Timeline',
  market: 'Market And Comps',
  constraints: 'Legal And Operating Context',
  evidence: 'Evidence Explorer',
  memo: 'Printable Memo',
};

const marketFilterLabels: Record<MarketFilter, string> = {
  all: 'All',
  active: 'Active',
  leased: 'Leased / Off-Market',
  historical: 'Historical',
  calibration: 'Calibration',
};

const parseRoute = (hash: string): RouteKey => {
  const cleaned = hash.replace('#', '').replace('/', '');
  if (
    cleaned === 'recommendation' ||
    cleaned === 'units' ||
    cleaned === 'building-history' ||
    cleaned === 'market' ||
    cleaned === 'constraints' ||
    cleaned === 'evidence' ||
    cleaned === 'memo'
  ) {
    return cleaned;
  }
  return 'overview';
};

const sourceLookup = new Map(sources.map((source) => [source.id, source]));

const buildCitations = (
  sourceIds?: string[],
  citationRefs?: CitationRef[]
): CitationRef[] => {
  if (citationRefs?.length) {
    return citationRefs;
  }

  return (sourceIds ?? []).map((sourceId) => ({
    sourceId,
    label: `[${sourceId}]`,
  }));
};

const latestAsk = (comp: ComparableRecord) =>
  comp.currentAsk ?? comp.lifecycle[comp.lifecycle.length - 1]?.ask ?? 'Not surfaced';

const lifecycleSummary = (comp: ComparableRecord) =>
  comp.lifecycle
    .map((event) => `${event.date} ${event.ask ? `${event.ask} ` : ''}${event.status.replace('_', ' ')}`)
    .join(' · ');

const statusTone = (status: CompOutcomeStatus) => {
  switch (status) {
    case 'active':
      return 'green';
    case 'leased_confirmed':
      return 'blue';
    case 'off_market_inferred':
      return 'amber';
    case 'historical':
      return 'muted';
    default:
      return 'muted';
  }
};

const filterComparables = (filter: MarketFilter) => {
  switch (filter) {
    case 'active':
      return comparables.filter((comp) => comp.outcomeStatus === 'active');
    case 'leased':
      return comparables.filter(
        (comp) =>
          comp.outcomeStatus === 'off_market_inferred' ||
          comp.outcomeStatus === 'leased_confirmed'
      );
    case 'historical':
      return comparables.filter((comp) => comp.outcomeStatus === 'historical');
    case 'calibration':
      return comparables.filter((comp) => comp.role === 'calibration');
    default:
      return comparables;
  }
};

const sameUnitComp = comparables.find((comp) => comp.id === 'comp-unit1');
const sameStreetComp = comparables.find((comp) => comp.id === 'comp-roque-25-6');
const floorComp = comparables.find((comp) => comp.id === 'comp-shelley-4');
const ceilingComp = comparables.find((comp) => comp.id === 'comp-seminary-1f');
const leasedHighlights = comparables.filter((comp) =>
  ['comp-unit1', 'comp-roque-25-6', 'comp-shelley-6', 'comp-seminary-1f'].includes(
    comp.id
  )
);

const SourceCitations = ({
  sourceIds,
  citationRefs,
  onOpenSource,
}: {
  sourceIds?: string[];
  citationRefs?: CitationRef[];
  onOpenSource: (source: SourceRecord) => void;
}) => {
  const citations = buildCitations(sourceIds, citationRefs);

  return (
    <span className="citation-group">
      {citations.map((citation) => {
        const source = sourceLookup.get(citation.sourceId);
        if (!source) {
          return null;
        }

        return (
          <span key={`${citation.sourceId}-${citation.context ?? 'default'}`} className="citation-pill">
            <a
              className="citation-chip"
              href={source.url}
              target="_blank"
              rel="noreferrer noopener"
              title={`Open ${source.title}`}
            >
              {citation.label ?? `[${citation.sourceId}]`}
            </a>
          </span>
        );
      })}
    </span>
  );
};

const ConfidenceBar = ({
  label,
  value,
}: {
  label: string;
  value: number;
}) => (
  <div className="confidence-row">
    <div className="confidence-meta">
      <span>{label}</span>
      <strong>{value}%</strong>
    </div>
    <div className="confidence-track">
      <div className="confidence-fill" style={{ width: `${value}%` }} />
    </div>
  </div>
);

const CompCard = ({
  comp,
  onOpenSource,
}: {
  comp: ComparableRecord;
  onOpenSource: (source: SourceRecord) => void;
}) => (
  <article className="highlight-card">
    <div className="panel-head">
      <strong>{comp.label}</strong>
      <span className={`pill status-pill ${statusTone(comp.outcomeStatus)}`}>
        {comp.outcomeLabel}
      </span>
    </div>
    <p className="key-metric">{latestAsk(comp)}</p>
    <p>{comp.fit}</p>
    <small>{comp.adjustment}</small>
    <p className="small-note">{lifecycleSummary(comp)}</p>
    <SourceCitations sourceIds={comp.sourceIds} onOpenSource={onOpenSource} />
  </article>
);

function App() {
  const [route, setRoute] = useState<RouteKey>(() =>
    typeof window === 'undefined' ? 'overview' : parseRoute(window.location.hash)
  );
  const [, setActiveSource] = useState<SourceRecord | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState('unit-1');
  const [evidenceFilter, setEvidenceFilter] = useState('all');
  const [marketFilter, setMarketFilter] = useState<MarketFilter>('all');
  const [selectedCompId, setSelectedCompId] = useState('comp-unit1');

  useEffect(() => {
    const handleHashChange = () => {
      setRoute(parseRoute(window.location.hash));
    };

    window.addEventListener('hashchange', handleHashChange);
    if (!window.location.hash) {
      window.location.hash = '#overview';
    }

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const selectedUnit = useMemo(
    () => units.find((unit) => unit.id === selectedUnitId) ?? units[0],
    [selectedUnitId]
  );

  const filteredEvidence = useMemo(() => {
    if (evidenceFilter === 'all') {
      return evidenceRecords;
    }
    return evidenceRecords.filter((record) => record.subject === evidenceFilter);
  }, [evidenceFilter]);

  const filteredComps = useMemo(() => filterComparables(marketFilter), [marketFilter]);

  useEffect(() => {
    if (!filteredComps.some((comp) => comp.id === selectedCompId)) {
      setSelectedCompId(filteredComps[0]?.id ?? comparables[0].id);
    }
  }, [filteredComps, selectedCompId]);

  const selectedComp = useMemo(
    () =>
      filteredComps.find((comp) => comp.id === selectedCompId) ??
      comparables.find((comp) => comp.id === selectedCompId) ??
      comparables[0],
    [filteredComps, selectedCompId]
  );

  const overviewStats = [
    { label: 'Units in building', value: '6' },
    { label: 'Units with direct public trace', value: '3' },
    { label: 'Sources indexed', value: sources.length.toString() },
    { label: 'Lifecycle comps indexed', value: comparables.length.toString() },
  ];

  const marketSummary = [
    {
      label: 'Active comps',
      value: comparables.filter((comp) => comp.outcomeStatus === 'active').length.toString(),
      note: 'Open asks still competing for demand.',
    },
    {
      label: 'Leased / off-market',
      value: leasedOffMarketComps.length.toString(),
      note: 'Removed-listing evidence used as a clearing-price proxy.',
    },
    {
      label: 'Confirmed leased',
      value: comparables
        .filter((comp) => comp.outcomeStatus === 'leased_confirmed')
        .length.toString(),
      note: 'Only explicit public lease confirmations belong here.',
    },
    {
      label: 'Same-street comps',
      value: comparables
        .filter((comp) => comp.address.includes('Roque Moraes'))
        .length.toString(),
      note: 'Highest micro-area relevance.',
    },
  ];

  const sourceSubjects = Array.from(
    new Set(evidenceRecords.map((record) => record.subject))
  );

  const renderOverview = () => (
    <section className="route-panel">
      <div className="hero-card">
        <div>
          <p className="eyebrow">17 Roque Moraes Ct · Mill Valley, CA 94941</p>
          <h1>Rental Intelligence Dashboard</h1>
          <p className="hero-copy">
            Public-source pricing intelligence for Unit 1, including exact-unit
            history, same-street leased/off-market proxies, active competition,
            internal 2BR calibration, and full source links.
          </p>
          <div className="hero-actions">
            <a className="primary-action" href="#recommendation">
              Open recommendation
            </a>
            <button
              className="secondary-action"
              type="button"
              onClick={() => window.print()}
            >
              Print memo
            </button>
          </div>
        </div>

        <div className="hero-aside">
          <div className="stat-stack">
            {overviewStats.map((stat) => (
              <div key={stat.label} className="stat-card">
                <span>{stat.label}</span>
                <strong>{stat.value}</strong>
              </div>
            ))}
          </div>
          <div className="pulse-card">
            <span>Target unit</span>
            <strong>Unit 1</strong>
            <p>
              Balanced starting ask remains <strong>$2,895</strong>, but the
              current site now shows both live competition and removed-listing
              evidence instead of relying on active asks alone.
            </p>
          </div>
        </div>
      </div>

      <div className="grid two-up">
        <article className="panel-card">
          <div className="panel-head">
            <h2>Pricing Ladder</h2>
            <span className="pill">As of {lastRefresh}</span>
          </div>
          <div className="scenario-stack">
            {recommendationScenarios.map((scenario) => (
              <div key={scenario.id} className="scenario-card">
                <div className="scenario-header">
                  <div>
                    <span className="scenario-label">{scenario.label}</span>
                    <strong>{scenario.askRent}</strong>
                  </div>
                  <span className="scenario-confidence">{scenario.confidence}</span>
                </div>
                <p>{scenario.posture}</p>
                <small>Effective range {scenario.effectiveRent}</small>
                <SourceCitations
                  sourceIds={scenario.sourceIds}
                  onOpenSource={setActiveSource}
                />
              </div>
            ))}
          </div>
        </article>

        <article className="panel-card">
          <div className="panel-head">
            <h2>Confidence Snapshot</h2>
            <span className="pill alert-pill">
              {contradictions.length} open contradictions
            </span>
          </div>
          <ConfidenceBar label="Same-unit evidence" value={83} />
          <ConfidenceBar label="Same-street evidence" value={79} />
          <ConfidenceBar label="Active market read" value={71} />
          <ConfidenceBar label="Leased/off-market proxy clarity" value={76} />
          <p className="panel-note">
            Confidence is stronger than the earlier active-only read because the
            market page now layers removed-listing evidence and lifecycle
            history. It still stops short of high because off-market is not the
            same as confirmed leased.
          </p>
        </article>
      </div>

      <article className="panel-card">
        <div className="panel-head">
          <h2>Evidence Tiers</h2>
          <span className="pill muted-pill">Direct chips open the live source</span>
        </div>
        <div className="highlight-grid">
          {sameUnitComp ? (
            <CompCard comp={sameUnitComp} onOpenSource={setActiveSource} />
          ) : null}
          {sameStreetComp ? (
            <CompCard comp={sameStreetComp} onOpenSource={setActiveSource} />
          ) : null}
          {floorComp ? <CompCard comp={floorComp} onOpenSource={setActiveSource} /> : null}
          {ceilingComp ? <CompCard comp={ceilingComp} onOpenSource={setActiveSource} /> : null}
        </div>
      </article>

      <div className="grid two-up">
        <article className="panel-card">
          <div className="panel-head">
            <h2>Key Claims</h2>
          </div>
          <ul className="clean-list">
            <li>
              Exact-unit evidence shows Unit 1 visibly tested at <strong>$2,675</strong>{' '}
              before coming off market faster after the cut to <strong>$2,575</strong>.
              <SourceCitations
                sourceIds={['S1', 'S2', 'S3']}
                onOpenSource={setActiveSource}
              />
            </li>
            <li>
              The strongest same-street 1BR analog at <strong>25 Roque Moraes Ct Apt 6</strong>{' '}
              resisted <strong>$2,700</strong> and showed better traction only after a move to{' '}
              <strong>$2,600</strong>.
              <SourceCitations sourceIds={['S24']} onOpenSource={setActiveSource} />
            </li>
            <li>
              Floor comps on Shelley mostly define the downside band rather than
              the likely clearing band for Unit 1 because they are smaller and weaker,
              clustering closer to roughly <strong>$2,000-$2,300</strong>.
              <SourceCitations
                sourceIds={['S18', 'S33', 'S34', 'S35']}
                onOpenSource={setActiveSource}
              />
            </li>
            <li>
              Premium ceiling comps near Seminary imply a practical ceiling around{' '}
              <strong>$2,995-$3,100</strong>, with <strong>$3,200</strong> behaving more
              like a test ask than a stable clearing level.
              <SourceCitations
                sourceIds={['S36', 'S37', 'S38']}
                onOpenSource={setActiveSource}
              />
            </li>
          </ul>
        </article>

        <article className="panel-card">
          <div className="panel-head">
            <h2>Trace Status</h2>
          </div>
          <div className="unit-grid">
            {units.map((unit) => (
              <button
                key={unit.id}
                className={`unit-chip ${selectedUnitId === unit.id ? 'selected' : ''}`}
                type="button"
                onClick={() => {
                  setSelectedUnitId(unit.id);
                  window.location.hash = '#units';
                }}
              >
                <strong>{unit.label}</strong>
                <span>{unit.traceStatus}</span>
              </button>
            ))}
          </div>
        </article>
      </div>
    </section>
  );

  const renderRecommendation = () => (
    <section className="route-panel">
      <div className="panel-card">
        <div className="panel-head">
          <h2>Unit 1 Recommendation</h2>
          <span className="pill">Balanced target: $2,895</span>
        </div>
        <div className="price-band">
          {recommendationScenarios.map((scenario) => (
            <article key={scenario.id} className="price-band-card">
              <div className="price-band-top">
                <span>{scenario.label}</span>
                <strong>{scenario.askRent}</strong>
              </div>
              <p>{scenario.rationale}</p>
              <small>Effective band {scenario.effectiveRent}</small>
              <ul className="trigger-list">
                {scenario.triggers.map((trigger) => (
                  <li key={trigger}>{trigger}</li>
                ))}
              </ul>
              <SourceCitations
                sourceIds={scenario.sourceIds}
                onOpenSource={setActiveSource}
              />
            </article>
          ))}
        </div>
      </div>

      <div className="grid two-up">
        <article className="panel-card">
          <div className="panel-head">
            <h2>Evidence Hierarchy</h2>
          </div>
          <ul className="clean-list">
            <li>
              <strong>Same unit:</strong> the best direct signal remains the 2025
              Unit 1 sequence from <strong>$2,675</strong> to <strong>$2,575</strong>.
              <SourceCitations sourceIds={['S1', 'S2', 'S3']} onOpenSource={setActiveSource} />
            </li>
            <li>
              <strong>Same street:</strong> 25 Roque Moraes Apt 6 hit visible resistance at{' '}
              <strong>$2,700</strong> and improved at <strong>$2,600</strong>, which keeps
              the best public same-street cluster around <strong>$2,575-$2,600</strong>.
              <SourceCitations sourceIds={['S24']} onOpenSource={setActiveSource} />
            </li>
            <li>
              <strong>Nearby floor:</strong> Shelley walk-up 1BRs clear meaningfully lower,
              which protects against over-reading weaker stock as a direct cap.
              <SourceCitations
                sourceIds={['S18', 'S33', 'S34', 'S35']}
                onOpenSource={setActiveSource}
              />
            </li>
            <li>
              <strong>Nearby ceiling:</strong> Seminary condo product defines the upper edge,
              but those asks require stronger package quality than Unit 1’s public baseline,
              especially in-unit laundry, elevator/garage access, and condo-grade finish.
              <SourceCitations
                sourceIds={['S36', 'S37', 'S38']}
                onOpenSource={setActiveSource}
              />
            </li>
            <li>
              <strong>2BR calibration:</strong> same-building and same-street 2BR traces keep
              the internal ladder relatively tight and discourage an overly aggressive 1BR premium,
              because once Unit 1 moves too close to upper-$2,700s renters can cross-shop 2BRs.
              <SourceCitations
                sourceIds={['S4', 'S7', 'S31', 'S39', 'S45', 'S47']}
                onOpenSource={setActiveSource}
              />
            </li>
          </ul>
        </article>

        <article className="panel-card">
          <div className="panel-head">
            <h2>Action Tree</h2>
          </div>
          <div className="decision-tree">
            <div>
              <span>Launch</span>
              <strong>$2,895</strong>
            </div>
            <div>
              <span>Day 7-10 with weak response</span>
              <strong>Add concession first</strong>
            </div>
            <div>
              <span>If still soft after concession</span>
              <strong>Move effective rent toward $2,845</strong>
            </div>
            <div>
              <span>Fast lease mode</span>
              <strong>Start near $2,745</strong>
            </div>
          </div>
          <p className="panel-note">
            The recommendation narrative now makes the clearing-price distinction explicit:
            removed listings are treated as off-market inferred unless a source directly says leased.
          </p>
        </article>
      </div>
    </section>
  );

  const renderUnits = () => (
    <section className="route-panel">
      <div className="unit-layout">
        <aside className="unit-sidebar panel-card">
          <div className="panel-head">
            <h2>Units</h2>
          </div>
          <div className="unit-nav-row">
            {units.map((unit) => (
              <button
                key={unit.id}
                className={`unit-nav ${selectedUnitId === unit.id ? 'selected' : ''}`}
                type="button"
                onClick={() => setSelectedUnitId(unit.id)}
              >
                <strong>{unit.label}</strong>
                <span>{unit.traceStatus}</span>
              </button>
            ))}
          </div>
        </aside>

        <div className="unit-detail">
          <article className="panel-card">
            <div className="panel-head">
              <h2>{selectedUnit.label}</h2>
              <span className="pill">{selectedUnit.status}</span>
            </div>
            <p className="panel-note">{selectedUnit.baselineNotes}</p>
            <div className="tag-row">
              {selectedUnit.inferredFeatures.map((feature) => (
                <span key={feature} className="feature-tag">
                  {feature}
                </span>
              ))}
            </div>
          </article>

          <article className="panel-card">
            <div className="panel-head">
              <h2>Strict Unit Ledger</h2>
              <span className="pill muted-pill">{selectedUnit.traceStatus}</span>
            </div>
            {selectedUnit.ledger.length ? (
              <div className="table-wrap">
                <table className="responsive-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Price</th>
                      <th>Event</th>
                      <th>Portals</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedUnit.ledger.map((event) => (
                      <tr key={`${selectedUnit.id}-${event.date}-${event.eventType}`}>
                        <td data-label="Date">{event.date}</td>
                        <td data-label="Price">{event.price}</td>
                        <td data-label="Event">{event.eventType}</td>
                        <td data-label="Portals">{event.portals.join(', ')}</td>
                        <td data-label="Notes">
                          {event.notes}
                          <SourceCitations
                            sourceIds={event.sourceIds}
                            onOpenSource={setActiveSource}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <strong>No documented unit-specific public events found.</strong>
                <p>
                  This page stays strict. Building-level or inferential material is
                  shown below instead of being promoted into the ledger.
                </p>
              </div>
            )}
          </article>

          <article className="panel-card">
            <div className="panel-head">
              <h2>Supporting Evidence</h2>
            </div>
            <ul className="clean-list">
              {selectedUnit.supportingEvidence.map((item) => (
                <li key={item.summary}>
                  {item.summary}
                  <SourceCitations
                    sourceIds={item.sourceIds}
                    onOpenSource={setActiveSource}
                  />
                </li>
              ))}
            </ul>
            {selectedUnit.portalsSearched ? (
              <p className="panel-note">
                Portals searched: {selectedUnit.portalsSearched.join(', ')}.
              </p>
            ) : null}
          </article>
        </div>
      </div>
    </section>
  );

  const renderBuildingHistory = () => (
    <section className="route-panel">
      <div className="grid two-up">
        <article className="panel-card">
          <div className="panel-head">
            <h2>Building Fact Inventory</h2>
          </div>
          <div className="table-wrap">
            <table className="responsive-table fact-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Chosen value</th>
                  <th>Confidence</th>
                  <th>Contradiction level</th>
                </tr>
              </thead>
              <tbody>
                {buildingFacts.map((fact) => (
                  <tr key={fact.field}>
                    <td data-label="Field">{fact.field}</td>
                    <td data-label="Chosen value">{fact.chosenValue}</td>
                    <td data-label="Confidence">{fact.confidence}</td>
                    <td data-label="Contradiction level">
                      {fact.contradiction}
                      <SourceCitations
                        sourceIds={fact.sourceIds}
                        onOpenSource={setActiveSource}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="panel-card">
          <div className="panel-head">
            <h2>Contradiction Register</h2>
          </div>
          <div className="contradiction-stack">
            {contradictions.map((item) => (
              <div key={item.field} className="contradiction-card">
                <div className="contradiction-head">
                  <strong>{item.field}</strong>
                  <span className={`severity ${item.severity.toLowerCase()}`}>
                    {item.severity}
                  </span>
                </div>
                <p>{item.values}</p>
                <small>{item.resolution}</small>
                <SourceCitations
                  sourceIds={item.sourceIds}
                  onOpenSource={setActiveSource}
                />
              </div>
            ))}
          </div>
        </article>
      </div>

      <article className="panel-card">
        <div className="panel-head">
          <h2>Building Timeline</h2>
        </div>
        <div className="timeline">
          {buildingTimeline.map((event) => (
            <div key={`${event.date}-${event.title}`} className="timeline-entry">
              <span className="timeline-date">{event.date}</span>
              <div className="timeline-card">
                <strong>{event.title}</strong>
                <p>{event.details}</p>
                <SourceCitations
                  sourceIds={event.sourceIds}
                  onOpenSource={setActiveSource}
                />
              </div>
            </div>
          ))}
        </div>
      </article>
    </section>
  );

  const renderMarket = () => (
    <section className="route-panel">
      <div className="grid market-summary-grid">
        {marketSummary.map((item) => (
          <article key={item.label} className="summary-card">
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <p>{item.note}</p>
          </article>
        ))}
      </div>

      <div className="grid two-up">
        <article className="panel-card">
          <div className="panel-head">
            <h2>Comp Interpretation</h2>
          </div>
          <ul className="clean-list">
            <li>
              Same-unit and same-street removed-listing evidence are still the highest-value
              clearing-price proxies for Unit 1.
              <SourceCitations sourceIds={['S1', 'S3', 'S24']} onOpenSource={setActiveSource} />
            </li>
            <li>
              Shelley product defines the lower walk-up floor; Seminary defines the richer
              condo-grade ceiling; neither should be treated as the exact answer by itself.
              <SourceCitations
                sourceIds={['S18', 'S33', 'S34', 'S35', 'S36', 'S37']}
                onOpenSource={setActiveSource}
              />
            </li>
            <li>
              Same-building and same-street 2BRs keep the ladder disciplined and show how little
              room there is for a 1BR to outrun the surrounding stock before 2BR cross-shopping starts.
              <SourceCitations
                sourceIds={['S4', 'S7', 'S31', 'S39', 'S41', 'S45', 'S47']}
                onOpenSource={setActiveSource}
              />
            </li>
          </ul>
        </article>

        <article className="panel-card">
          <div className="panel-head">
            <h2>Active 1BR Band</h2>
            <span className="pill">Current asks only</span>
          </div>
          <div className="comp-band">
            {activeComps.map((comp) => (
              <div key={comp.address} className="comp-visual">
                <div className="comp-price">{comp.ask}</div>
                <div className="comp-body">
                  <strong>{comp.address}</strong>
                  <span>
                    {comp.bedBath} · {comp.sqft} sqft
                  </span>
                  <p>{comp.fit}</p>
                  <SourceCitations
                    sourceIds={comp.sourceIds}
                    onOpenSource={setActiveSource}
                  />
                </div>
              </div>
            ))}
          </div>
        </article>
      </div>

      <article className="panel-card">
        <div className="panel-head">
          <h2>Leased / Off-Market Comparable Evidence</h2>
          <span className="pill alert-pill">Removed listing is not equal to leased</span>
        </div>
        <div className="highlight-grid">
          {leasedHighlights.map((comp) => (
            <CompCard key={comp.id} comp={comp} onOpenSource={setActiveSource} />
          ))}
        </div>
      </article>

      <article className="panel-card">
        <div className="panel-head">
          <h2>Comparable Register</h2>
          <span className="pill muted-pill">
            {marketFilterLabels[marketFilter]} · {filteredComps.length} records
          </span>
        </div>
        <div className="market-filter-row">
          {(Object.keys(marketFilterLabels) as MarketFilter[]).map((filter) => (
            <button
              key={filter}
              type="button"
              className={`filter-pill ${marketFilter === filter ? 'selected' : ''}`}
              onClick={() => setMarketFilter(filter)}
            >
              {marketFilterLabels[filter]}
            </button>
          ))}
        </div>
        <div className="table-wrap">
          <table className="responsive-table">
            <thead>
              <tr>
                <th>Comparable</th>
                <th>Status</th>
                <th>Latest ask</th>
                <th>Fit</th>
                <th>Visible range</th>
                <th>Lifecycle</th>
              </tr>
            </thead>
            <tbody>
              {filteredComps.map((comp) => (
                <tr
                  key={comp.id}
                  className={selectedComp.id === comp.id ? 'selected-row' : undefined}
                >
                  <td data-label="Comparable">
                    <button
                      type="button"
                      className="table-select"
                      onClick={() => setSelectedCompId(comp.id)}
                    >
                      <strong>{comp.label}</strong>
                      <span>{comp.address}</span>
                    </button>
                  </td>
                  <td data-label="Status">
                    <span className={`pill status-pill ${statusTone(comp.outcomeStatus)}`}>
                      {comp.outcomeLabel}
                    </span>
                  </td>
                  <td data-label="Latest ask">{latestAsk(comp)}</td>
                  <td data-label="Fit">{comp.fit}</td>
                  <td data-label="Visible range">
                    {comp.firstSeenAt ?? 'Unknown'}
                    {comp.lastSeenAt ? ` to ${comp.lastSeenAt}` : ''}
                    {comp.daysVisible ? <div className="small-note">{comp.daysVisible}</div> : null}
                  </td>
                  <td data-label="Lifecycle">
                    {lifecycleSummary(comp)}
                    <SourceCitations
                      sourceIds={comp.sourceIds}
                      citationRefs={comp.citationRefs}
                      onOpenSource={setActiveSource}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>

      <div className="grid two-up">
        <article className="panel-card comp-detail-panel">
          <div className="panel-head">
            <h2>Selected Lifecycle</h2>
            <span className={`pill status-pill ${statusTone(selectedComp.outcomeStatus)}`}>
              {selectedComp.outcomeLabel}
            </span>
          </div>
          <p className="panel-note">{selectedComp.address}</p>
          <div className="tag-row">
            <span className="feature-tag">{selectedComp.bedBath}</span>
            <span className="feature-tag">{selectedComp.sqft} sqft</span>
            <span className="feature-tag">{selectedComp.role}</span>
            <span className="feature-tag">{selectedComp.outcomeConfidence} confidence</span>
          </div>
          <p>{selectedComp.features}</p>
          <p className="small-note">{selectedComp.adjustment}</p>
          <div className="timeline compact-timeline">
            {selectedComp.lifecycle.map((event) => (
              <div
                key={`${selectedComp.id}-${event.date}-${event.status}`}
                className="timeline-entry"
              >
                <span className="timeline-date">{event.date}</span>
                <div className="timeline-card">
                  <strong>
                    {event.ask ? `${event.ask} · ` : ''}
                    {event.status.replace('_', ' ')}
                  </strong>
                  <p>{event.note}</p>
                  <SourceCitations
                    sourceIds={event.sourceIds}
                    onOpenSource={setActiveSource}
                  />
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="panel-card">
          <div className="panel-head">
            <h2>Historical Discipline Notes</h2>
          </div>
          <ul className="clean-list">
            {historicalComps.map((comp) => (
              <li key={comp.name}>
                <strong>{comp.name}:</strong> {comp.readThrough}
                <div className="small-note">{comp.observedFacts}</div>
                <SourceCitations
                  sourceIds={comp.sourceIds}
                  onOpenSource={setActiveSource}
                />
              </li>
            ))}
          </ul>
        </article>
      </div>
    </section>
  );

  const renderConstraints = () => (
    <section className="route-panel">
      <article className="panel-card">
        <div className="panel-head">
          <h2>Public-Source Context, Not Legal Advice</h2>
          <span className="pill alert-pill">Use for pricing framing only</span>
        </div>
        <div className="constraint-stack">
          {constraints.map((constraint) => (
            <div key={constraint.title} className="constraint-card">
              <strong>{constraint.title}</strong>
              <p>{constraint.takeaway}</p>
              <small>{constraint.applicability}</small>
              <SourceCitations
                sourceIds={constraint.sourceIds}
                onOpenSource={setActiveSource}
              />
            </div>
          ))}
        </div>
      </article>
    </section>
  );

  const renderEvidence = () => (
    <section className="route-panel">
      <div className="grid two-up">
        <article className="panel-card">
          <div className="panel-head">
            <h2>Evidence Register</h2>
          </div>
          <div className="filter-row">
            <label htmlFor="evidence-filter">Filter by subject</label>
            <select
              id="evidence-filter"
              value={evidenceFilter}
              onChange={(event) => setEvidenceFilter(event.target.value)}
            >
              <option value="all">All subjects</option>
              {sourceSubjects.map((subject) => (
                <option key={subject} value={subject}>
                  {subject}
                </option>
              ))}
            </select>
          </div>
          <div className="table-wrap">
            <table className="responsive-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Claim</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvidence.map((record) => (
                  <tr key={record.id}>
                    <td data-label="ID">{record.id}</td>
                    <td data-label="Subject">{record.subject}</td>
                    <td data-label="Category">{record.category}</td>
                    <td data-label="Claim">
                      {record.claim}
                      <SourceCitations
                        sourceIds={record.sourceIds}
                        onOpenSource={setActiveSource}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="panel-card source-panel">
          <div className="panel-head">
            <h2>Source Library</h2>
            <span className="pill muted-pill">Title links open externally</span>
          </div>
          <div className="source-library">
            {sources.map((source) => (
              <div key={source.id} className="source-library-item">
                <div>
                  <span className={`source-type ${source.sourceType}`}>
                    {source.sourceType.replace('_', ' ')}
                  </span>
                  <strong>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="source-title-link"
                    >
                      {source.id} · {source.title}
                    </a>
                  </strong>
                  <small>
                    {source.domain} · Observed {source.observedAt}
                  </small>
                  {source.drawerSummary ? <p>{source.drawerSummary}</p> : null}
                </div>
              </div>
            ))}
          </div>
        </article>
      </div>
    </section>
  );

  const renderMemo = () => (
    <section className="route-panel memo-panel">
      <article className="panel-card memo-card">
        <p className="eyebrow">Printable Memo</p>
        <h1>Unit 1 Rental Pricing Memo</h1>
        <p>
          Property: <strong>17 Roque Moraes Ct, Mill Valley, CA 94941</strong>
        </p>
        <p>
          Recommendation: launch Unit 1 around <strong>$2,895</strong> with a
          balanced strategy, reassess after 7-10 days, and prefer a concession
          before a permanent face-rent cut.
          <SourceCitations
            sourceIds={['S1', 'S3', 'S18', 'S20', 'S21', 'S24', 'S36', 'S37']}
            onOpenSource={setActiveSource}
          />
        </p>

        <h2>Why this range</h2>
        <ul className="clean-list">
          <li>
            Exact-address evidence for Unit 1 improved after the March 2025 cut
            from $2,675 to $2,575.
            <SourceCitations sourceIds={['S1', 'S2', 'S3']} onOpenSource={setActiveSource} />
          </li>
          <li>
            Same-street and nearby removed-listing evidence clusters in the
            mid-$2,500s to mid-$2,600s for the strongest Roque Moraes-style 1BR analogs.
            <SourceCitations
              sourceIds={['S24', 'S33', 'S34', 'S35']}
              onOpenSource={setActiveSource}
            />
          </li>
          <li>
            $2,995 and above is already occupied by stronger renovation or
            condo-grade amenity packages and should be treated as a stretch case only.
            <SourceCitations
              sourceIds={['S21', 'S22', 'S23', 'S36', 'S37']}
              onOpenSource={setActiveSource}
            />
          </li>
        </ul>

        <h2>Leased / Off-Market Comparable Evidence</h2>
        <ul className="clean-list">
          <li>
            <strong>17 Roque Moraes Ct #1:</strong> public unit history shows
            $2,675 first, then $2,575 before removal. Labeled as off-market inferred, not confirmed leased.
            <SourceCitations sourceIds={['S1', 'S2', 'S3']} onOpenSource={setActiveSource} />
          </li>
          <li>
            <strong>25 Roque Moraes Ct Apt 6:</strong> visible resistance at
            $2,700 and improved traction at $2,600 is the best same-street 1BR check.
            <SourceCitations sourceIds={['S24']} onOpenSource={setActiveSource} />
          </li>
          <li>
            <strong>Shelley floor comps:</strong> weaker nearby 1BR walk-ups
            stair-stepped down into the high-$1,000s to low-$2,000s before removal.
            <SourceCitations
              sourceIds={['S18', 'S33', 'S34', 'S35']}
              onOpenSource={setActiveSource}
            />
          </li>
          <li>
            <strong>Seminary ceiling comps:</strong> richer condo product around
            $2,995 to $3,200 helps mark the ceiling, but not the likely answer for Unit 1.
            <SourceCitations
              sourceIds={['S36', 'S37', 'S38']}
              onOpenSource={setActiveSource}
            />
          </li>
        </ul>

        <h2>Supporting same-building history</h2>
        <ul className="clean-list">
          <li>
            Unit 1: listed at $2,675, then $2,575 before removal.
            <SourceCitations sourceIds={['S1', 'S2', 'S3']} onOpenSource={setActiveSource} />
          </li>
          <li>
            Unit 2: listed at $2,875, then cut to $2,775 before removal.
            <SourceCitations sourceIds={['S4', 'S5', 'S6']} onOpenSource={setActiveSource} />
          </li>
          <li>
            Unit 4: a 2BR at $3,000 disappeared in roughly three to four weeks.
            <SourceCitations sourceIds={['S7', 'S8']} onOpenSource={setActiveSource} />
          </li>
        </ul>

        <h2>Constraints and handling notes</h2>
        <ul className="clean-list">
          {constraints.map((constraint) => (
            <li key={constraint.title}>
              <strong>{constraint.title}:</strong> {constraint.takeaway}
              <SourceCitations
                sourceIds={constraint.sourceIds}
                onOpenSource={setActiveSource}
              />
            </li>
          ))}
        </ul>
      </article>
    </section>
  );

  const renderedRoute = {
    overview: renderOverview(),
    recommendation: renderRecommendation(),
    units: renderUnits(),
    'building-history': renderBuildingHistory(),
    market: renderMarket(),
    constraints: renderConstraints(),
    evidence: renderEvidence(),
    memo: renderMemo(),
  }[route];

  const handleRouteSelect = (nextRoute: RouteKey) => {
    window.location.hash = `#${nextRoute}`;
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-copy">
          <p className="eyebrow">Private owner dashboard</p>
          <strong>{routeTitles[route]}</strong>
        </div>
        <label className="mobile-route-picker">
          <span>Section</span>
          <select
            value={route}
            onChange={(event) => handleRouteSelect(event.target.value as RouteKey)}
            aria-label="Jump to section"
          >
            {routeOrder.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <div className="topbar-meta">
          <span>Last refreshed {lastRefresh}</span>
          <button type="button" className="secondary-action" onClick={() => window.print()}>
            Print
          </button>
        </div>
      </header>

      <div className="page-frame">
        <nav className="sidebar">
          <div className="sidebar-card sidebar-nav-card">
            <p className="eyebrow">Navigation</p>
            <div className="sidebar-nav">
              {routeOrder.map((item) => (
                <a
                  key={item.key}
                  className={`nav-link ${route === item.key ? 'active' : ''}`}
                  href={`#${item.key}`}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>
          <div className="sidebar-card">
            <p className="eyebrow">Pricing anchor</p>
            <strong>$2,895 balanced</strong>
            <p className="sidebar-copy">
              Same-unit and same-street removed-listing evidence now sits beside
              active asks, rather than behind them.
            </p>
          </div>
        </nav>
        <main className="content">{renderedRoute}</main>
      </div>
    </div>
  );
}

export default App;
